import type { IContextMenuModule } from '../types';

/**
 * Modul für Kontextmenü-Funktionalität
 * Verwaltet die Positionierung und Überlappungsprüfung von Kontextmenüs
 */
const ContextMenuModule: IContextMenuModule = {
    contextMenuClassName: 'context-menu',
    dashboardLayoutBodyClassName: 'dashboard-layout-body',

    /**
     * Prüft, ob ein Kontextmenü am rechten Rand des Dashboard-Bodies überlappt,
     * berücksichtigt dabei alle Parent-Kontextmenüs und deren Breiten
     * @param contextMenuId - Die ID des zu prüfenden Kontextmenüs
     * @returns true, wenn das Menü überlappt, sonst false
     */
    overlapContextMenuDashboardRight(contextMenuId: string): boolean {
        const contextMenu = document.getElementById(contextMenuId);
        if (!contextMenu) {
            //console.warn(`Kontextmenü mit ID "${contextMenuId}" nicht gefunden`);
            return false;
        }

        const dashboardBody = document.querySelector<HTMLElement>(`.${this.dashboardLayoutBodyClassName}`);
        if (!dashboardBody) {
            //console.warn(`Dashboard-Body mit Klasse "${this.dashboardLayoutBodyClassName}" nicht gefunden`);
            return false;
        }

        const menuRect = contextMenu.getBoundingClientRect();
        const dashboardRect = dashboardBody.getBoundingClientRect();

        // Sammle alle Parent-Kontextmenüs und deren Breiten
        let totalMenuWidth = menuRect.width;
        let mainMenuLeft = menuRect.left;
        let currentElement: HTMLElement | null = contextMenu.parentElement;

        // Durchlaufe den DOM-Baum nach oben und sammle alle Parent-Kontextmenüs
        while (currentElement && currentElement !== document.body) {
            // Prüfe, ob das aktuelle Element ein Kontextmenü ist
            if (currentElement.classList.contains(this.contextMenuClassName)) {
                const parentRect = currentElement.getBoundingClientRect();
                totalMenuWidth += parentRect.width;
                mainMenuLeft = parentRect.left;
            }
            
            currentElement = currentElement.parentElement;
        }

        // Berechne die rechte Position des Menüs
        const menuRight = mainMenuLeft + totalMenuWidth;
        const dashboardRight = dashboardRect.right;

        // Prüfe, ob das Menü über den rechten Rand hinausragt
        const overlaps = menuRight > dashboardRight;

        return overlaps;
    },

    /**
     * Registriert einen Click-Outside-Handler für ein Kontextmenü
     * @param elementId - Die ID des Kontextmenüs
     * @param dotNetHelper - Die .NET-Objektreferenz
     * @param methodName - Der Name der aufzurufenden .NET-Methode
     */
    registerClickOutside(elementId: string, dotNetHelper: any, methodName: string): void {
        const element = document.getElementById(elementId);
        if (!element) {
            console.warn(`Element mit ID "${elementId}" nicht gefunden`);
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!element.contains(target)) {
                dotNetHelper.invokeMethodAsync(methodName);
            }
        };

        // Handler für späteres Cleanup speichern
        (element as any)._clickOutsideHandler = handleClickOutside;
        document.addEventListener('click', handleClickOutside, true);
    },

    /**
     * Entfernt den Click-Outside-Handler für ein Kontextmenü
     * @param elementId - Die ID des Kontextmenüs
     */
    unregisterClickOutside(elementId: string): void {
        const element = document.getElementById(elementId);
        if (!element || !(element as any)._clickOutsideHandler) {
            return;
        }

        document.removeEventListener('click', (element as any)._clickOutsideHandler, true);
        delete (element as any)._clickOutsideHandler;
    }
};

export default ContextMenuModule;
